import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Eager: homepage is the most common landing surface — keep it in the main bundle
// so the LCP for "/" doesn't wait for an extra chunk fetch.
import LandingPage from "@/pages/LandingPage";
import AnalyticsTracker from "@/lib/AnalyticsTracker";
import ScrollToTop from "@/lib/ScrollToTop";
import CookieConsent from "@/components/landing/CookieConsent";
import MailingListPopup from "@/components/landing/MailingListPopup";
import DSCLicenseGate from "@/components/license/DSCLicenseGate";

// Lazy: every other route splits into its own chunk. Tiny "loading" UI fills
// the brief gap before the route chunk arrives (typically <100ms on modern
// networks because chunks are tiny and prefetched after the main bundle).
const FunnelsPage                       = lazy(() => import("@/pages/FunnelsPage"));
const WorkshopPage                      = lazy(() => import("@/pages/WorkshopPage"));
const VaultPage                         = lazy(() => import("@/pages/VaultPage"));
const SocialMediaViralityPage           = lazy(() => import("@/pages/SocialMediaViralityPage"));
const SocialMediaUncensoredPage         = lazy(() => import("@/pages/SocialMediaUncensoredPage"));
const BookACallPage                     = lazy(() => import("@/pages/BookACallPage"));
const FreeConsultationPage              = lazy(() => import("@/pages/audits/FreeConsultationPage"));
const MarketingAuditPage                = lazy(() => import("@/pages/audits/MarketingAuditPage"));
const StrategyCallPage                  = lazy(() => import("@/pages/audits/StrategyCallPage"));
const MarketingAuditStrategyCallPage    = lazy(() => import("@/pages/audits/MarketingAuditStrategyCallPage"));
const VaultLoginPage                    = lazy(() => import("@/pages/VaultLoginPage"));
const AdminResendPage                   = lazy(() => import("@/pages/AdminResendPage"));
const AdminProductRegistryPage          = lazy(() => import("@/pages/AdminProductRegistryPage"));
const AdminBookingsPage                 = lazy(() => import("@/pages/AdminBookingsPage"));
const SeoCoursePage                     = lazy(() => import("@/pages/SeoCoursePage"));
const SeoCourseLearnPage                = lazy(() => import("@/pages/SeoCourseLearnPage"));
const CreativeBriefPage                 = lazy(() => import("@/pages/creativeBrief/CreativeBriefPage"));
const CreativeBriefDashboard            = lazy(() => import("@/pages/creativeBrief/CreativeBriefDashboard"));
const AdminOutreachPage                 = lazy(() => import("@/pages/AdminOutreachPage"));
const EnrollPage                        = lazy(() => import("@/pages/EnrollPage"));
const OnboardingPage                    = lazy(() => import("@/pages/portal/OnboardingPage"));
const PortalDashboardPage               = lazy(() => import("@/pages/portal/PortalDashboardPage"));
const PortalPointsPage                  = lazy(() => import("@/pages/portal/PortalPointsPage"));
const MarketingAuditWizardPage          = lazy(() => import("@/pages/portal/MarketingAuditWizardPage"));
const NotFoundPage                      = lazy(() => import("@/pages/NotFoundPage"));
const HustleZonePage                    = lazy(() => import("@/pages/podcast/HustleZonePage"));
const FreeApplicationPage               = lazy(() => import("@/pages/podcast/FreeApplicationPage"));
const StandardGuestFeaturePage          = lazy(() => import("@/pages/podcast/StandardGuestFeaturePage"));
const PremiumStorySpotlightPage         = lazy(() => import("@/pages/podcast/PremiumStorySpotlightPage"));
const VipTransformationFeaturePage      = lazy(() => import("@/pages/podcast/VipTransformationFeaturePage"));
const WaitingRoom                       = lazy(() => import("@/pages/WaitingRoom"));
const WatchPage                         = lazy(() => import("@/pages/WatchPage"));
const CheckoutPage                      = lazy(() => import("@/pages/CheckoutPage"));
const TermsAndConditionsPage            = lazy(() => import("@/pages/legal/TermsAndConditionsPage"));
const RefundPolicyPage                  = lazy(() => import("@/pages/legal/RefundPolicyPage"));
const PrivacyPolicyPage                 = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const CookiePolicyPage                  = lazy(() => import("@/pages/legal/CookiePolicyPage"));
const BrandTransformationWorkshopPage   = lazy(() => import("@/pages/BrandTransformationWorkshopPage"));
const WebsiteRevenueRegenerationPage    = lazy(() => import("@/pages/WebsiteRevenueRegenerationPage"));
const BrandIdentityEbookPage            = lazy(() => import("@/pages/BrandIdentityEbookPage"));
const BrandIdentitySystemPage           = lazy(() => import("@/pages/BrandIdentitySystemPage"));
const MarketplacePage                   = lazy(() => import("@/pages/MarketplacePage"));
const DataDiagramsPage                  = lazy(() => import("@/pages/DataDiagramsPage"));
const UnlimitedDesignPartnerPage        = lazy(() => import("@/pages/UnlimitedDesignPartnerPage"));
const TieUpLooseEndsPage                = lazy(() => import("@/pages/TieUpLooseEndsPage"));
const RevenueMasterclassPage            = lazy(() => import("@/pages/RevenueMasterclassPage"));
const TierLandingPage                   = lazy(() => import("@/pages/TierLandingPage"));
const LocationPage                      = lazy(() => import("@/pages/seo/LocationPage"));
const IndustryPage                      = lazy(() => import("@/pages/seo/IndustryPage"));
const LocationsIndexPage                = lazy(() => import("@/pages/seo/SeoIndexPage").then(m => ({ default: m.LocationsIndexPage })));
const IndustriesIndexPage               = lazy(() => import("@/pages/seo/SeoIndexPage").then(m => ({ default: m.IndustriesIndexPage })));
const CaseStudiesIndexPage              = lazy(() => import("@/pages/seo/SeoIndexPage").then(m => ({ default: m.CaseStudiesIndexPage })));
const GoldTieProgramPage                = lazy(() => import("@/pages/GoldTieProgramPage"));
const GrowthPointsPage                  = lazy(() => import("@/pages/GrowthPointsPage"));
const GrowthPointsWelcomePage           = lazy(() => import("@/pages/GrowthPointsWelcomePage"));
const AboutBowTieKreativePage           = lazy(() => import("@/pages/AboutBowTieKreativePage"));
const SupportPage                       = lazy(() => import("@/pages/SupportFormPage"));
const WorkshopIntakePage                = lazy(() => import("@/pages/WorkshopIntakePage"));
const BookingDatesPage                  = lazy(() => import("@/pages/BookingDatesPage"));
const PossibilityLatticePage            = lazy(() => import("@/pages/PossibilityLatticePage"));
const PossibilityLatticeViewPage        = lazy(() => import("@/pages/PossibilityLatticeViewPage"));
const PredictionEnginePage              = lazy(() => import("@/pages/PredictionEnginePage"));
const PredictionEngineViewPage          = lazy(() => import("@/pages/PredictionEngineViewPage"));
const SeoReportPage                     = lazy(() => import("@/pages/SeoReportPage"));
const MarketingAuditFreePage            = lazy(() => import("@/pages/MarketingAuditFreePage"));
const DigitalStemCellLandingPage        = lazy(() => import("@/pages/products/DigitalStemCellLandingPage"));
const DSCTierCheckoutPage               = lazy(() => import("@/pages/products/DSCTierCheckoutPage"));
const DocsIndexPage                     = lazy(() => import("@/pages/DocsIndexPage"));
const DocsArticlePage                   = lazy(() => import("@/pages/DocsArticlePage"));
const AdminEmailDripsPage               = lazy(() => import("@/pages/AdminEmailDripsPage"));
const AdminApiTokensPage                = lazy(() => import("@/pages/AdminApiTokensPage"));
const AdminPagesPage                    = lazy(() => import("@/pages/AdminPagesPage"));
const AdminMenusPage                    = lazy(() => import("@/pages/AdminMenusPage"));
const DynamicPage                       = lazy(() => import("@/pages/DynamicPage"));
const BlogIndexPage                     = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage                      = lazy(() => import("@/pages/BlogPostPage"));
const AdminBlogPage                     = lazy(() => import("@/pages/AdminBlogPage"));
const AdminMasterProductPage            = lazy(() => import("@/pages/AdminMasterProductPage"));
const AdminWorkflowsPage                = lazy(() => import("@/pages/AdminWorkflowsPage"));
const AdminCMSHubPage                   = lazy(() => import("@/pages/AdminCMSHubPage"));
const AdminTestimonialsPage             = lazy(() => import("@/pages/AdminTestimonialsPage"));
const AdminPortfolioPage                = lazy(() => import("@/pages/AdminPortfolioPage"));
const EmailUnsubscribePage              = lazy(() => import("@/pages/EmailUnsubscribePage"));
const CancelSubscriptionPage            = lazy(() => import("@/pages/CancelSubscriptionPage"));

// Minimal fallback while a route chunk loads. Matches the dark brand BG so
// there's no white flash. Avoids spinner clutter — bias toward "loaded fast"
// over "look busy".
const RouteFallback = () => (
  <div data-testid="route-fallback" className="min-h-screen bg-ink-900" aria-busy="true" />
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <CookieConsent />
          <MailingListPopup />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/funnels" element={<FunnelsPage />} />
              <Route path="/workshop" element={<WorkshopPage />} />
              <Route path="/vault" element={<VaultPage />} />
              <Route path="/social-media-virality" element={<SocialMediaViralityPage />} />
              <Route path="/social-media-uncensored" element={<SocialMediaUncensoredPage />} />
              <Route path="/book-a-call" element={<BookACallPage />} />
              <Route path="/book-a-call/free-consultation" element={<FreeConsultationPage />} />
              <Route path="/book-a-call/marketing-audit" element={<MarketingAuditPage />} />
              <Route path="/book-a-call/strategy-call" element={<StrategyCallPage />} />
              <Route path="/book-a-call/marketing-audit-strategy-call" element={<MarketingAuditStrategyCallPage />} />
              <Route path="/verify-license" element={<Navigate to="/onboarding" replace />} />
              <Route path="/vault/login" element={<VaultLoginPage />} />
              <Route path="/admin/resend" element={<AdminResendPage />} />
              <Route path="/admin/portal-products" element={<AdminProductRegistryPage />} />
              <Route path="/admin/bookings" element={<AdminBookingsPage />} />
              <Route path="/seo-course" element={<SeoCoursePage />} />
              <Route path="/seo-course/learn" element={<SeoCourseLearnPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/portal" element={<PortalDashboardPage />} />
              <Route path="/portal/points" element={<PortalPointsPage />} />
              <Route path="/portal/audit/:token" element={<MarketingAuditWizardPage />} />
              {/* Hustle Zone — landing page only. Application intake is now
                  handled entirely inside SuiteDash; buyers receive a SuiteDash
                  application form via email. Legacy slugs redirect to the hub. */}
              <Route path="/the-hustle-zone" element={<HustleZonePage />} />
              <Route path="/the-hustle-zone/free-application" element={<FreeApplicationPage />} />
              <Route path="/the-hustle-zone/standard-guest-feature" element={<StandardGuestFeaturePage />} />
              <Route path="/the-hustle-zone/premium-story-spotlight" element={<PremiumStorySpotlightPage />} />
              <Route path="/the-hustle-zone/vip-transformation-feature" element={<VipTransformationFeaturePage />} />
              <Route path="/checkout/:planSlug" element={<CheckoutPage />} />
              <Route path="/webinar/waiting-room/:token" element={<WaitingRoom />} />
              <Route path="/webinar/watch/:token" element={<WatchPage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/brand-transformation-workshop" element={<BrandTransformationWorkshopPage />} />
              <Route path="/website-revenue-regeneration" element={<WebsiteRevenueRegenerationPage />} />
              <Route path="/brand-identity-ebook" element={<BrandIdentityEbookPage />} />
              <Route path="/complete-brand-identity-system" element={<BrandIdentitySystemPage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/support" element={<SupportPage formKey="general" />} />
              <Route path="/support/:slug" element={<SupportPage />} />
              <Route path="/workshop/:slug/intake" element={<WorkshopIntakePage />} />
              <Route path="/workshop/:slug/dates" element={<BookingDatesPage />} />
              <Route path="/booking/:slug" element={<BookingDatesPage mode="single" />} />
              {/* Digital Stem Cell™ — Landing pages (sales) at canonical URLs */}
              <Route path="/seo-report"             element={<DigitalStemCellLandingPage slug="seo-report" />} />
              <Route path="/marketing-audit-free"   element={<DigitalStemCellLandingPage slug="marketing-audit-free" />} />
              <Route path="/possibility-lattice"    element={<DigitalStemCellLandingPage slug="possibility-lattice" />} />
              <Route path="/prediction-engine"      element={<DigitalStemCellLandingPage slug="prediction-engine" />} />

              {/* Digital Stem Cell™ — Utilities (interactive tools) */}
              <Route path="/utilities/seo-report"           element={<SeoReportPage />} />
              <Route path="/utilities/marketing-audit"      element={<MarketingAuditFreePage />} />
              <Route path="/utilities/strategy-engine"      element={
                <DSCLicenseGate utility="strategy-engine" purchaseUrl="/possibility-lattice" tierLabel="the Strategy Engine">
                  <PossibilityLatticePage />
                </DSCLicenseGate>
              } />
              <Route path="/utilities/strategy-engine/v/:id" element={<PossibilityLatticeViewPage />} />
              <Route path="/utilities/prediction-engine"    element={
                <DSCLicenseGate utility="prediction-engine" purchaseUrl="/prediction-engine" tierLabel="the Prediction Engine">
                  <PredictionEnginePage />
                </DSCLicenseGate>
              } />
              <Route path="/utilities/prediction-engine/v/:id" element={<PredictionEngineViewPage />} />

              {/* Digital Stem Cell™ — Per-SKU checkout post pages (embed Zylvie inline) */}
              <Route path="/dsc/:tier_slug" element={<DSCTierCheckoutPage />} />

              {/* Legacy share URLs — keep working so existing share links don't 404 */}
              <Route path="/possibility-lattice/v/:id" element={<PossibilityLatticeViewPage />} />
              <Route path="/prediction-engine/v/:id" element={<PredictionEngineViewPage />} />
              <Route path="/admin/diagrams" element={<DataDiagramsPage />} />
              <Route path="/unlimited-design-partner" element={<UnlimitedDesignPartnerPage />} />
              <Route path="/tie-up-loose-ends" element={<TieUpLooseEndsPage />} />
              <Route path="/revenue-masterclass" element={<RevenueMasterclassPage />} />

              {/* Per-SKU tier landing pages (Bow Tie Websites + Concierge
                  Funnels + GrowthPoints). All routes share /pages/TierLandingPage.jsx
                  and pull copy from /data/tierLandings.js — keeps bundles tiny. */}
              <Route path="/websites/:tier" element={<TierLandingPage />} />
              <Route path="/funnels/:tier" element={<TierLandingPage />} />
              <Route path="/growth-points/:tier" element={<TierLandingPage />} />

              {/* Programmatic SEO landing pages — cities, industries, case studies.
                  Powered by /pages/seo/* components reading from /data/seo*.js */}
              <Route path="/locations" element={<LocationsIndexPage />} />
              <Route path="/locations/:slug" element={<LocationPage />} />
              <Route path="/industries" element={<IndustriesIndexPage />} />
              <Route path="/industries/:slug" element={<IndustryPage />} />
              {/* /case-studies retained as a passthrough redirect — content was
                  removed because earlier rows were placeholder. The component
                  redirects to /locations on mount. */}
              <Route path="/case-studies" element={<CaseStudiesIndexPage />} />
              {/* /creative-brief — restored 2026-05-23 (iteration 96). License-gated
                  multi-step intake form; PDF is rendered via MarkupGo on submit
                  and attached to both buyer + admin notification emails. */}
              <Route path="/creative-brief" element={<CreativeBriefPage />} />
              <Route path="/creative-brief/dashboard" element={<CreativeBriefDashboard />} />
              <Route path="/gold-tie-program" element={<GoldTieProgramPage />} />
              <Route path="/growth-points" element={<GrowthPointsPage />} />
              <Route path="/growth-points/welcome" element={<GrowthPointsWelcomePage />} />
              <Route path="/about-bow-tie-kreative" element={<AboutBowTieKreativePage />} />
              <Route path="/docs" element={<DocsIndexPage />} />
              <Route path="/docs/:slug" element={<DocsArticlePage />} />
              <Route path="/admin/email-drips" element={<AdminEmailDripsPage />} />
              <Route path="/admin/api-tokens" element={<AdminApiTokensPage />} />
              <Route path="/admin/pages" element={<AdminPagesPage />} />
              <Route path="/admin/menus" element={<AdminMenusPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/admin/blog" element={<AdminBlogPage />} />
              <Route path="/admin/master-product" element={<AdminMasterProductPage />} />
              <Route path="/admin/workflows" element={<AdminWorkflowsPage />} />
              <Route path="/admin/cms" element={<AdminCMSHubPage />} />
              <Route path="/admin/testimonials" element={<AdminTestimonialsPage />} />
              <Route path="/admin/portfolio" element={<AdminPortfolioPage />} />
              {/* Outreach tools — meeting invites + serialized enrollment intakes
                  for non-buyers. Public `/enroll?token=…` is NOT linked anywhere
                  and only renders when a valid invite token is present. */}
              <Route path="/admin/outreach" element={<AdminOutreachPage />} />
              <Route path="/enroll" element={<EnrollPage />} />
              <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />
              <Route path="/cancel-subscription" element={<CancelSubscriptionPage />} />
              {/* CMS catch-all — DynamicPage hits /api/cms/pages/by-slug/<slug>
                  and renders the matching template, or NotFoundPage if no row. */}
              <Route path="*" element={<DynamicPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
